/**
 * Tax Engine — single fiscal calculator for catalog, purchases, sales, POS, invoicing.
 *
 * priceMode=net:  amount is before tax → tax = net * rate, gross = net + tax
 * priceMode=final: amount includes tax → net = gross / (1+rate), tax = gross - net
 *
 * Non-taxable categories: tax=0, net=gross=amount (no silent 21%).
 */

import type { PriceMode, TaxCalculateInput, TaxCalculateResult, TaxCategory } from "./types.js";
import { roundMoney, sumMoney } from "./money.js";

export function effectiveTaxRate(category: TaxCategory, taxRate: number): number {
  if (category !== "taxable") return 0;
  if (!Number.isFinite(taxRate) || taxRate < 0) return 0;
  return taxRate;
}

export function calculateTax(input: TaxCalculateInput): TaxCalculateResult {
  const quantity = Number.isFinite(input.quantity) && (input.quantity as number) > 0 ? Number(input.quantity) : 1;
  const ratePct = effectiveTaxRate(input.taxCategory, input.taxRate);
  const rate = ratePct / 100;
  const amount = Number.isFinite(input.amount) ? Number(input.amount) : 0;

  let unitNet: number;
  let unitTax: number;
  let unitGross: number;

  if (rate <= 0) {
    unitNet = roundMoney(amount);
    unitTax = 0;
    unitGross = unitNet;
  } else if (input.priceMode === "net") {
    unitNet = roundMoney(amount);
    unitTax = roundMoney(unitNet * rate);
    unitGross = roundMoney(unitNet + unitTax);
  } else {
    // final / IVA incluido
    unitGross = roundMoney(amount);
    unitNet = roundMoney(unitGross / (1 + rate));
    unitTax = roundMoney(unitGross - unitNet);
  }

  return {
    unitNet,
    unitTax,
    unitGross,
    netAmount: roundMoney(unitNet * quantity),
    taxAmount: roundMoney(unitTax * quantity),
    grossAmount: roundMoney(unitGross * quantity),
    taxRate: ratePct,
    taxCategory: input.taxCategory,
    priceMode: input.priceMode,
    quantity,
  };
}

/** Aggregate line results — sum nets/taxes/gross with shared rounding. */
export function aggregateTaxLines(lines: TaxCalculateResult[]): {
  netAmount: number;
  taxAmount: number;
  grossAmount: number;
} {
  return {
    netAmount: sumMoney(lines.map((l) => l.netAmount)),
    taxAmount: sumMoney(lines.map((l) => l.taxAmount)),
    grossAmount: sumMoney(lines.map((l) => l.grossAmount)),
  };
}

/**
 * Normalize two supplier quotes to the same basis (net) for comparison.
 * Does not invent rates — caller must pass known rate or 0.
 */
export function normalizeToNet(amount: number, priceMode: PriceMode, taxCategory: TaxCategory, taxRate: number): number {
  return calculateTax({ amount, priceMode, taxCategory, taxRate }).netAmount;
}

/**
 * Commercial margin on net basis when possible.
 * margin = (saleNet - costNet) / saleNet  (0..1 scale as fraction)
 * Returns null if saleNet <= 0.
 */
export function marginOnNet(saleNet: number, costNet: number): number | null {
  if (!Number.isFinite(saleNet) || saleNet <= 0) return null;
  if (!Number.isFinite(costNet)) return null;
  return (saleNet - costNet) / saleNet;
}

/** Back-compat wrapper used by invoicing (gross → net). */
export function priceWithoutIva(precioConIva: number, alicuota: number): number {
  return calculateTax({
    amount: precioConIva,
    priceMode: "final",
    taxCategory: alicuota > 0 ? "taxable" : "exempt",
    taxRate: alicuota,
  }).netAmount;
}
